import { readOriginalVideo } from '@src/editor/dubbing/readOriginalVideo';
import { KaraokeClip } from '@src/editor/models/karaoke/KaraokeClip';
import { KaraokeTrack } from '@src/editor/models/karaoke/KaraokeTrack';
import { resolvePunctuationLineBreak } from '@src/editor/services/captionLineBreakStyle';
import {
  buildDubRecord,
  dubCaptionInputs,
  dubRowOptions,
} from '@src/editor/services/dubCaptionRows';
import {
  resolveConfiguredMaxCharLength,
  segmentTokens,
  segmentTokensByPhrase,
  squashOrphanedSegments,
} from '@src/editor/services/karaokeSegmentation';
import { normalizeLangKey } from '@src/utility/bcp47';
import { v4 as uuid } from 'uuid';

import { AbstractCommand } from '../AbstractCommand';

import type { ClipSpan } from '@src/editor/dubbing/DubPlacementService';
import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';
import type { TranscriptToken } from '@src/editor/types';
import type { VideoEditor } from '@src/editor/VideoEditor';
import type { DubManifestDTO } from '@src/repositories/DubbingRepository';
import type { TranslationPhrase } from '@src/types.ts/translation';

const ORIGINAL_LANGUAGE = 'original';

/**
 * ProduceKaraokeCommand - Creates a new KaraokeTrack from the active transcript
 *
 * This command:
 * 1. Gets the active transcript tokens
 * 2. Segments them into KaraokeClips using logic similar to KaraokeProducer.assembleKaraokeClips
 * 3. Creates a new KaraokeTrack with these clips
 * 4. Adds the track to the timeline
 */
export class ProduceKaraokeCommand extends AbstractCommand {
  getName(): string {
    return 'ProduceKaraokeCommand';
  }

  private karaokeConfig: KaraokeConfig;
  private createdTrack: KaraokeTrack | null = null;
  private trackNumber: number;
  private _shouldCreateUndoRedoEntry: boolean;

  constructor(
    editor: VideoEditor,
    trackNumber: number,
    createUndoRedoEntry: boolean = true,
  ) {
    super(editor);

    // Get default karaoke config or throw if not available
    const defaultConfig = this.editor.getDefaultKaraokeConfig();
    if (!defaultConfig) {
      throw new Error('No default karaoke config available');
    }
    this.karaokeConfig = defaultConfig;
    this.trackNumber = trackNumber;
    this._shouldCreateUndoRedoEntry = createUndoRedoEntry;
  }

  shouldCreateUndoRedoEntry(): boolean {
    return this._shouldCreateUndoRedoEntry;
  }

  execute(): boolean {
    // Get active transcript tokens (removed tokens are already filtered out)
    const activeTranscriptTokens = this.editor.getActiveTranscriptNew();
    if (!activeTranscriptTokens || activeTranscriptTokens.length === 0) {
      console.error('No active transcript tokens available');
      return false;
    }

    // PLAT-9621: phrase-boundary segmentation when caption language is a
    // translation. The editor's translation resolver returns the phrase
    // set for the configured language; falling back to the legacy
    // character-length path keeps the original-language flow untouched
    // (and the legacy 'english' SubtitlesProcessor flow until T48/T49).
    const phrases = this.getTranslationPhrases();

    const karaokeClips: KaraokeClip[] = phrases
      ? this.produceTranslatedClips(activeTranscriptTokens, phrases)
      : this.produceOriginalClips(activeTranscriptTokens);

    if (karaokeClips.length === 0) {
      console.error('No karaoke clips generated');
      return false;
    }

    // Create KaraokeTrack
    this.createdTrack = new KaraokeTrack({
      id: String(this.trackNumber),
      type: 'text',
      karaokeClips,
    });

    this.attachCaptionDub(this.createdTrack);

    // Add track to timeline
    this.editor.timeline.addExistingTrack(this.createdTrack);

    // Show the set the voice calls for: dub-timed rows while the dub is heard.
    this.editor.syncCaptionSetToVoice();

    // Mark editor as dirty
    this.editor.markDirty('karaoke_produced');

    return true;
  }

  /**
   * PLAT-11905 — a dubbed language gets its dub-timed set too, whatever the
   * voice position, from the description in hand; and a fresh description is
   * asked for, so a re-dub since it was fetched is picked up when it lands.
   *
   * Never fails the produce: if the held description cannot be read into the
   * dub-timed set, the track keeps only its source-timed captions and the
   * produce finishes exactly as it did before the dub-timed set existed.
   */
  private attachCaptionDub(track: KaraokeTrack): void {
    try {
      const dub = this.describeCaptionDub();
      if (!dub) return;
      if (dub.spans.length > 0) {
        track.dubRecord = buildDubRecord(
          dub.language,
          dubCaptionInputs(dub),
          dubRowOptions(this.karaokeConfig),
        );
      }
      this.editor.dubSource.requestRefresh(dub.language);
    } catch (error) {
      console.warn(
        'Could not build the dub-timed captions; keeping source-timed captions only',
        error,
      );
    }
  }

  /**
   * The dub held for the caption language, with the clip's kept stretches, or
   * `null` when the captions are in the original language or no description
   * for their language is held.
   */
  private describeCaptionDub(): {
    language: string;
    manifest: DubManifestDTO;
    renditionUrls: Record<string, string>;
    spans: ClipSpan[];
  } | null {
    const captionLanguage = this.karaokeConfig.language;
    if (!captionLanguage || captionLanguage === ORIGINAL_LANGUAGE) return null;
    const holder = this.editor.dubSource;
    const wanted = normalizeLangKey(captionLanguage);
    const language = holder
      .languages()
      .find(held => normalizeLangKey(held) === wanted);
    const described = language ? holder.describe(language) : undefined;
    if (!language || !described) return null;
    const { spans } = readOriginalVideo(this.editor.timeline, probe =>
      holder.isOriginalVideoElement(probe),
    );
    return {
      language,
      manifest: described.manifest,
      renditionUrls: described.renditionUrls,
      spans,
    };
  }

  undo(): boolean {
    if (!this.createdTrack) {
      return false;
    }

    // Remove the karaoke track
    this.editor.timeline.removeTrack(this.trackNumber.toString());
    this.createdTrack = null;

    // Mark editor as dirty
    this.editor.markDirty('karaoke_removed');

    return true;
  }

  getDescription(): string {
    return 'Produce karaoke track from active transcript';
  }

  /**
   * Resolve the phrase set for the configured caption language. Returns
   * `null` when the configured language is `'original'`, or when no
   * resolver is registered, or when the resolver has no data for that
   * language — all cases route to the legacy character-length path.
   */
  private getTranslationPhrases(): readonly TranslationPhrase[] | null {
    const language = this.karaokeConfig.language;
    if (!language || language === ORIGINAL_LANGUAGE) return null;
    const phrases = this.editor.getTranslationPhrasesForLanguage(language);
    if (!phrases || phrases.length === 0) return null;
    return phrases;
  }

  private produceOriginalClips(
    activeTranscriptTokens: TranscriptToken[],
  ): KaraokeClip[] {
    // Treat the default sentinel value the same as null — both mean
    // "use the segmenter's default line length" (see
    // resolveConfiguredMaxCharLength).
    const configuredMaxLength = resolveConfiguredMaxCharLength(
      this.karaokeConfig.transcript_maximum_length,
    );

    const punctuationLineBreak = resolvePunctuationLineBreak(
      this.karaokeConfig.line_break_style,
    );
    const karaokeSegments = segmentTokens(activeTranscriptTokens, {
      maxSegmentCharLength: configuredMaxLength,
      punctuationLineBreak,
    });

    const karaokeClips: KaraokeClip[] = karaokeSegments
      .map(segment =>
        this.createClipFromTokens(
          segment.tokens,
          segment.startTime,
          segment.endTime,
        ),
      )
      .filter((clip): clip is KaraokeClip => !!clip);

    // Orphan squashing runs at EVERY line length now, not just the default: a
    // custom length used to skip it entirely ("squashing would negate it"),
    // which stranded every short sentence tail on a row of its own. Passing the
    // cap keeps the length honest — a merge that would overflow the row is
    // refused, so the tail only rejoins where it actually fits.
    // Punctuation style skips it: every row ends where the punctuation says,
    // and squashing would glue short clauses ("Well,") back together.
    if (punctuationLineBreak) return karaokeClips;
    return squashOrphanedSegments(karaokeClips, configuredMaxLength);
  }

  private produceTranslatedClips(
    activeTranscriptTokens: TranscriptToken[],
    phrases: readonly TranslationPhrase[],
  ): KaraokeClip[] {
    const segments = segmentTokensByPhrase(
      activeTranscriptTokens,
      phrases,
      originalIndices => this.lookupTranslatedValue(phrases, originalIndices),
      {
        maxSegmentCharLength: resolveConfiguredMaxCharLength(
          this.karaokeConfig.transcript_maximum_length,
        ),
        punctuationLineBreak: resolvePunctuationLineBreak(
          this.karaokeConfig.line_break_style,
        ),
      },
    );

    return segments
      .map(segment =>
        this.createClipFromTokens(
          segment.tokens,
          segment.startTime,
          segment.endTime,
          segment.translatedText,
          segment.translatedPhrases,
          segment.phraseOriginalIndices,
          segment.startSecondsOffset,
          segment.endSecondsOffset,
          segment.siblingGroupId,
        ),
      )
      .filter((clip): clip is KaraokeClip => !!clip);
  }

  private lookupTranslatedValue(
    phrases: readonly TranslationPhrase[],
    originalIndices: number[],
  ): string | null {
    // Phrase identity is by exact `originalIndices` match; rely on the
    // segmentation pass passing through the same array reference per phrase
    // so this is a constant-time identity check in practice. The
    // value-equality fallback handles unexpected callers.
    const target =
      phrases.find(p => p.originalIndices === originalIndices) ??
      phrases.find(p => sameIndices(p.originalIndices, originalIndices));
    return target ? target.translatedValue : null;
  }

  /**
   * Create a KaraokeClip from token array
   * Includes all tokens (even muted ones) but marks muted tokens in hiddenTranscriptTokenIds
   */
  private createClipFromTokens(
    tokens: TranscriptToken[],
    startTime: number | null,
    endTime: number | null,
    translatedText?: string,
    translatedPhrases?: {
      text: string;
      ts: number;
      endTs: number;
      originalIndices?: number[];
    }[],
    phraseOriginalIndices?: number[],
    startSecondsOffset?: number,
    endSecondsOffset?: number,
    siblingGroupId?: string,
  ): KaraokeClip | null {
    if (tokens.length === 0 || startTime === null || endTime === null) {
      return null;
    }

    return new KaraokeClip({
      id: uuid(),
      transcriptTokens: tokens, // Include ALL tokens
      hiddenTranscriptTokenIds: [],
      ignoredByFiltersIds: [],
      karaokeConfig: this.karaokeConfig,
      track: this.trackNumber,
      translatedText,
      translatedPhrases,
      phraseOriginalIndices,
      // PLAT-11481 — sub-clips of an over-length phrase split share one
      // token bucket; offsets claim each clip's slice of its window.
      startSecondsOffset,
      endSecondsOffset,
      siblingGroupId,
    });
  }
}

function sameIndices(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
